/**
 * @fileoverview Agent definition for the world-element-chunked-extractor-v3 primitive.
 *
 * This agent is designed for processing large (epic-length) documents in chunks.
 * It combines detection (surface + inferential) and enrichment (descriptions)
 * in a single pass, taking a text chunk and previously extracted elements,
 * then outputting a merged list following attribute-level additivity rules.
 *
 * Key differences from the standard extract-world-elements-v3 pipeline:
 * - Accepts `textChunk` instead of `textInput`
 * - Accepts `partialElements` for incremental merging
 * - Accepts `chunkInfo` for context about position in document
 * - Single-pass detection + detailing (no separate detector/detailer steps)
 * - Prompt is optimized for amendment/merging behavior
 *
 * @category import.world-building.extraction
 * @type primitive
 */

import type { AppAgentDefinition } from '@/domains/agents/schemas/agent';
import { InputSchema, OutputSchema } from './schema';
import { prompt } from './prompt';

export const definition: AppAgentDefinition = {
  id: 'world-element-chunked-extractor-v3',
  kind: 'primitive',
  name: 'Chunked World Element Extractor',
  description: 'Extracts and enriches world elements from a text chunk, merging with previously extracted elements. Designed for processing epic-length documents.',
  category: 'import',
  defaultPrompt: prompt,
  inputSchema: InputSchema,
  outputSchema: OutputSchema,
  metadata: {
    status: 'active',
    type: 'primitive',
    addedIn: '2026-03',
    lastUpdated: '2026-03',
    taxonomyPath: 'import.world-building.extraction.chunked',
    usedBy: ['use-explore-world-chunked-extractor.ts'],
    relatedAgents: ['world-element-core-detector-v3', 'world-element-rare-detector-v3', 'world-element-detailer-v3'],
  },
};
